import { onMounted, onUnmounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useViewerItemStore } from '../store/useViewerItemStore';

const MAX_BUCKET_COUNT = 12;
const MIN_BUCKET_COUNT = 3;
// Roughly the rendered width of a marker pill (icon + count + padding) — used
// to derive how many buckets can fit side-by-side without overlapping.
export const MIN_MARKER_WIDTH_PX = 56;

export interface VideoTimeEvent {
  videoTime: number;
}

export interface VideoTimelineBucket<T extends VideoTimeEvent> {
  bucketIndex: number;
  events: T[];
  leftPercent: number;
  seekTime: number;
}

export const bucketCountForWidth = (containerWidthPx: number): number => {
  if (!containerWidthPx) {
    return MAX_BUCKET_COUNT;
  }

  const buckets = Math.floor(containerWidthPx / MIN_MARKER_WIDTH_PX);
  return Math.min(MAX_BUCKET_COUNT, Math.max(MIN_BUCKET_COUNT, buckets));
};

/**
 * Groups timestamped events into fixed buckets across the video's duration so
 * the scrubber overlay can show a handful of markers instead of one per event.
 * Each bucket keeps its events in input order. `bucketCount` defaults to the max
 * so callers that don't care about available screen width get the finest grouping.
 */
export const bucketByVideoTime = <T extends VideoTimeEvent>(events: T[], duration: number, bucketCount = MAX_BUCKET_COUNT): VideoTimelineBucket<T>[] => {
  if (!duration || events.length === 0) {
    return [];
  }

  const buckets = new Map<number, T[]>();
  events.forEach(event => {
    const bucketIndex = Math.min(bucketCount - 1, Math.floor((event.videoTime / duration) * bucketCount));
    const bucket = buckets.get(bucketIndex) || [];
    bucket.push(event);
    buckets.set(bucketIndex, bucket);
  });

  return Array.from(buckets.entries()).map(([bucketIndex, bucketEvents]) => {
    // seekTime jumps to the earliest event in the bucket; leftPercent sits at the
    // bucket's average time so a marker stays near the cluster rather than its first event.
    const times = bucketEvents.map(event => event.videoTime);
    const averageTime = times.reduce((sum, time) => sum + time, 0) / times.length;

    return {
      bucketIndex,
      events: bucketEvents,
      leftPercent: (averageTime / duration) * 100,
      seekTime: Math.min(...times),
    };
  });
};

/**
 * Finds the viewer's video element and video.js progress bar so an overlay can
 * teleport markers onto the scrubber, and tracks the video duration and the
 * progress bar width those markers are laid out against.
 */
export const useVideoTimeline = () => {
  const itemStore = useViewerItemStore();
  const { id } = storeToRefs(itemStore);

  const duration = ref(0);
  const progressHolder = ref<HTMLElement | null>(null);
  const containerWidth = ref(0);
  let viewerRoot: HTMLElement | null = null;
  let videoElement: HTMLVideoElement | null = null;
  let observer: MutationObserver | null = null;
  let resizeObserver: ResizeObserver | null = null;

  const updateDuration = () => {
    if (videoElement && !isNaN(videoElement.duration) && videoElement.duration > 0) {
      duration.value = videoElement.duration;
    }
  };

  const attachVideoListener = (video: HTMLVideoElement) => {
    if (videoElement === video) {
      return;
    }
    detachVideoListener();
    videoElement = video;
    video.addEventListener('durationchange', updateDuration);
    video.addEventListener('loadedmetadata', updateDuration);
    updateDuration();
  };

  const detachVideoListener = () => {
    if (videoElement) {
      videoElement.removeEventListener('durationchange', updateDuration);
      videoElement.removeEventListener('loadedmetadata', updateDuration);
      videoElement = null;
    }
  };

  const findTargets = () => {
    if (!viewerRoot) {
      return;
    }

    const video = viewerRoot.querySelector('video');
    if (video) {
      attachVideoListener(video);
    }

    if (!progressHolder.value) {
      progressHolder.value = viewerRoot.querySelector('.video-js .vjs-progress-holder');

      if (progressHolder.value) {
        resizeObserver = new ResizeObserver(([entry]) => {
          containerWidth.value = entry.contentRect.width;
        });
        resizeObserver.observe(progressHolder.value);
        containerWidth.value = progressHolder.value.clientWidth;
      }
    }

    if (videoElement && progressHolder.value) {
      observer?.disconnect();
      observer = null;
    }
  };

  // video.js can recreate the progress-holder DOM node on an item transition. The
  // MutationObserver above disconnects once it first finds both targets, so without
  // this reset a recreated node would leave the Teleport pointed at a detached
  // element with no rediscovery path.
  const resetTargets = () => {
    progressHolder.value = null;
    containerWidth.value = 0;

    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }

    if (!observer && viewerRoot) {
      observer = new MutationObserver(findTargets);
      observer.observe(viewerRoot, { childList: true, subtree: true });
    }
  };

  watch(id, () => {
    duration.value = 0;
    resetTargets();
  });

  const seekTo = (seekTime: number) => itemStore.setVideoTime(seekTime);

  onMounted(() => {
    viewerRoot = document.getElementById('viewer-content') || document.getElementById('main') || document.body;
    findTargets();

    if (videoElement && progressHolder.value) {
      return;
    }

    observer = new MutationObserver(findTargets);
    observer.observe(viewerRoot, { childList: true, subtree: true });
  });

  onUnmounted(() => {
    detachVideoListener();
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  });

  return { containerWidth, duration, progressHolder, seekTo };
};
